import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, unwrapArray } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import type { CalendarEventItem } from "@/types/task";
import { CalendarView } from "@/components/calendar/CalendarView";
import { MeetingTaskDialog } from "@/components/calendar/MeetingTaskDialog";
import { TaskDetailSheet } from "@/components/calendar/TaskDetailSheet";
import { CalendarConnectionsModal } from "@/components/calendar/CalendarConnectionsModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  CalendarDays,
  Plus,
  RefreshCw,
  User,
  Users,
  Settings2,
  CheckCircle2,
  AlertCircle,
  Video,
  Phone,
  Mail,
  CheckSquare,
} from "lucide-react";
import { useToast } from "@/context/ToastContext";

export default function CalendarPage() {
  const { user } = useAuth();
  const { error: toastError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL state
  const activeScope = searchParams.get("scope") || "mine";
  const typeFilter = searchParams.get("type") || "ALL";

  // Date range tracking (from FullCalendar datesSet)
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: new Date(new Date().setDate(1)),
    end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
  });

  const [includeExternal, setIncludeExternal] = useState(true);
  const [events, setEvents] = useState<CalendarEventItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{
    start: Date;
    end: Date;
    allDay: boolean;
  } | null>(null);

  const [selectedEvent, setSelectedEvent] = useState<CalendarEventItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Connection banner
  const [oauthBanner, setOauthBanner] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Manager or admin detection supporting SUPER_ADMIN, SALES_HEAD, etc.
  const rawRole =
    user?.UserRole?.RoleCode ||
    (user as any)?.user_role_code ||
    (user as any)?.role ||
    "";
  const roleCode = String(rawRole).toUpperCase();
  const dealScope = user?.data_scopes?.deal;
  const leadScope = user?.data_scopes?.lead;
  const activityScope = user?.data_scopes?.activity;
  const isManagerOrAdmin =
    roleCode === "SUPER_ADMIN" ||
    roleCode === "ADMIN" ||
    roleCode === "SALES_HEAD" ||
    roleCode === "SALES_MANAGER" ||
    activityScope === "team" ||
    activityScope === "all" ||
    dealScope === "team" ||
    dealScope === "all" ||
    leadScope === "team" ||
    leadScope === "all";

  // Check URL params on mount for OAuth redirects
  useEffect(() => {
    const connected = searchParams.get("connected");
    const provider = searchParams.get("provider");
    const error = searchParams.get("error");

    if (connected === "true" && provider) {
      setOauthBanner({
        type: "success",
        message: `Successfully connected and authorized your ${provider} Calendar! Events are syncing.`,
      });
      // Clean params
      const next = new URLSearchParams(searchParams);
      next.delete("connected");
      next.delete("provider");
      setSearchParams(next, { replace: true });
    } else if (error) {
      setOauthBanner({
        type: "error",
        message: `Calendar connection failed: ${decodeURIComponent(error)}`,
      });
      const next = new URLSearchParams(searchParams);
      next.delete("error");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchEvents = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("from", dateRange.start.toISOString());
      params.set("to", dateRange.end.toISOString());
      params.set("scope", activeScope);
      if (typeFilter !== "ALL") params.set("task_type", typeFilter);
      params.set("include_external", String(includeExternal));

      const res = await api.get(`/api/Calendar/Range?${params.toString()}`);
      const list = unwrapArray<CalendarEventItem>(res);
      setEvents(list);
    } catch (err) {
      console.warn("Failed to load calendar events:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, activeScope, typeFilter, includeExternal]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDatesSet = (start: Date, end: Date) => {
    setDateRange({ start, end });
  };

  const handleSelectSlot = (start: Date, end: Date, allDay: boolean) => {
    setSelectedSlot({ start, end, allDay });
    setCreateMeetingOpen(true);
  };

  const handleEventClick = (event: CalendarEventItem) => {
    setSelectedEvent(event);
    setDetailOpen(true);
  };

  const handleRescheduleEvent = async (
    id: string,
    start: Date,
    end?: Date,
    kind: "TASK" | "EXTERNAL" = "TASK",
  ) => {
    try {
      if (kind === "EXTERNAL") {
        await api.patch(`/api/Calendar/External/${id}`, {
          start: start.toISOString(),
          end: end ? end.toISOString() : undefined,
        });
      } else {
        await api.put(`/api/Task/Update/${id}`, {
          due_at: start.toISOString(),
          end_at: end ? end.toISOString() : undefined,
        });
      }
      fetchEvents();
    } catch (err: any) {
      toastError("Could not reschedule", err?.message || "Failed to reschedule event");
      throw err;
    }
  };

  const handleManualSync = async () => {
    setLoading(true);
    try {
      await api.post("/api/Calendar/Sync").catch((e) => {
        console.warn("Sync error:", e);
      });
      await fetchEvents();
    } finally {
      setLoading(false);
    }
  };

  const setScope = (scope: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("scope", scope);
    setSearchParams(next);
  };

  const setType = (type: string) => {
    const next = new URLSearchParams(searchParams);
    if (type !== "ALL") {
      next.set("type", type);
    } else {
      next.delete("type");
    }
    setSearchParams(next);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
              <CalendarDays className="size-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate">
                Calendar & Meeting Schedule
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">
                Visual agenda, Google & Outlook two-way sync, and client meeting invites.
              </p>
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {/* Mine / Team scope switch */}
          {isManagerOrAdmin ? (
            <div className="flex items-center p-1 rounded-lg border border-border bg-card shadow-2xs">
              <button
                type="button"
                onClick={() => setScope("mine")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  activeScope === "mine"
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <User className="size-3.5" />
                <span>My Calendar</span>
              </button>
              <button
                type="button"
                onClick={() => setScope("team")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  activeScope === "team"
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Users className="size-3.5" />
                <span>Team Calendar</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-muted-foreground gap-1.5 shadow-2xs">
              <User className="size-3.5 text-primary" />
              <span>My Calendar</span>
            </div>
          )}

          {/* Sync status & config modal trigger */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConnectionsOpen(true)}
            className="text-xs flex items-center gap-1.5"
          >
            <Settings2 className="size-3.5" />
            <span>Sync Accounts</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleManualSync}
            disabled={loading}
            title="Sync & refresh schedule"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>

          <Button
            size="sm"
            onClick={() => {
              setSelectedSlot(null);
              setCreateMeetingOpen(true);
            }}
            className="flex items-center gap-1.5 shadow-xs text-xs"
          >
            <Plus className="size-4" />
            <span>New Meeting</span>
          </Button>
        </div>
      </div>

      {/* OAuth Banner if redirected */}
      {oauthBanner && (
        <div
          className={`p-3.5 rounded-xl border flex items-center justify-between text-xs font-medium ${
            oauthBanner.type === "success"
              ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-900"
              : "bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200 border-red-200 dark:border-red-900"
          }`}
        >
          <div className="flex items-center gap-2">
            {oauthBanner.type === "success" ? (
              <CheckCircle2 className="size-4 shrink-0" />
            ) : (
              <AlertCircle className="size-4 shrink-0" />
            )}
            <span>{oauthBanner.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setOauthBanner(null)}
            className="text-muted-foreground hover:text-foreground font-bold px-2 py-0.5"
          >
            &times;
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-3">
        {/* Type pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setType("ALL")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              typeFilter === "ALL"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            All Items
          </button>
          <button
            type="button"
            onClick={() => setType("MEETING")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
              typeFilter === "MEETING"
                ? "bg-blue-600 text-white shadow-xs"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Video className="size-3.5" /> Meetings
          </button>
          <button
            type="button"
            onClick={() => setType("CALL")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
              typeFilter === "CALL"
                ? "bg-amber-600 text-white shadow-xs"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Phone className="size-3.5" /> Calls
          </button>
          <button
            type="button"
            onClick={() => setType("EMAIL")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
              typeFilter === "EMAIL"
                ? "bg-purple-600 text-white shadow-xs"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Mail className="size-3.5" /> Emails
          </button>
          <button
            type="button"
            onClick={() => setType("FOLLOW_UP")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
              typeFilter === "FOLLOW_UP"
                ? "bg-cyan-600 text-white shadow-xs"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <CheckSquare className="size-3.5" /> Follow-ups
          </button>
        </div>

        {/* Right side controls: External Calendars toggle & Schedule Meeting button */}
        <div className="flex items-center gap-3 text-xs shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-[11px] hidden md:inline">
              External Calendars (Google/Outlook)
            </span>
            <Switch
              checked={includeExternal}
              onCheckedChange={setIncludeExternal}
            />
          </div>

          <Button
            size="sm"
            onClick={() => {
              setSelectedSlot(null);
              setCreateMeetingOpen(true);
            }}
            className="text-xs h-8 gap-1.5 px-3 shadow-xs bg-primary text-primary-foreground font-semibold"
          >
            <Plus className="size-3.5" />
            <span>Schedule Meeting</span>
          </Button>
        </div>
      </div>

      {/* Empty schedule indicator */}
      {events.length === 0 && !loading && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3.5 rounded-xl border border-dashed border-border bg-card/60 text-xs">
          <div className="flex items-center gap-2.5 text-muted-foreground">
            <CalendarDays className="size-4 text-primary shrink-0" />
            <span>
              {activeScope === "team"
                ? "No team meetings or activities scheduled for this date range."
                : "No personal tasks or meetings scheduled for this date range."}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isManagerOrAdmin && activeScope === "mine" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScope("team")}
                className="text-xs h-7 gap-1"
              >
                <Users className="size-3" />
                <span>Switch to Team Calendar</span>
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                setSelectedSlot(null);
                setCreateMeetingOpen(true);
              }}
              className="text-xs h-7 gap-1 shadow-2xs"
            >
              <Plus className="size-3" />
              <span>Schedule Meeting</span>
            </Button>
          </div>
        </div>
      )}

      {/* Calendar Grid View */}
      <CalendarView
        events={events}
        loading={loading}
        onEventClick={handleEventClick}
        onSelectSlot={handleSelectSlot}
        onRescheduleEvent={handleRescheduleEvent}
        onDatesSet={handleDatesSet}
      />

      {/* Dialogs */}
      <MeetingTaskDialog
        open={createMeetingOpen}
        onOpenChange={setCreateMeetingOpen}
        initialStart={selectedSlot?.start}
        initialEnd={selectedSlot?.end}
        initialAllDay={selectedSlot?.allDay}
        onSaved={() => fetchEvents()}
      />

      <TaskDetailSheet
        event={selectedEvent}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={() => fetchEvents()}
        onDeleted={() => fetchEvents()}
      />

      <CalendarConnectionsModal
        open={connectionsOpen}
        onOpenChange={setConnectionsOpen}
        onConnectionsChanged={() => fetchEvents()}
      />
    </div>
  );
}
